import React, { useState } from 'react';
import BurgerMenuIcon from './BurgerMenuIcon';
import { Box, Container, IconButton, Stack, Typography, CircularProgress } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { theme, themeColors } from '../styles/theme';
import Fonts from './Fonts';
import CloseIcon from "@mui/icons-material/Close";
import PubSearchGoogle, { GooglePubResult } from './PubSearchGoogle';
import { getPubByGooglePlaceId, fetchMinimalMeshes, AppAuth } from '../API';
import extractCity from '../utils/cityExtractor';
import axios from 'axios';
import config from '../config';
import { trackHomePageExit, trackPubNotFound } from '../utils/analytics';
// Import logo directly
import suntapLogo from '../img/suntap_logo.svg';
import sunIcon from '../img/sun_icon.png';

interface TopNavProps {
    title: string;
}

// Define a constant for the header height to keep it consistent across the application
export const HEADER_HEIGHT = '10vh';
const MENU_ITEM_STYLE = {
    marginTop: '12px',
    padding: '12px',
    textAlign: 'center',
    borderRadius: '40px',
    color: themeColors.darkBackground,
    fontFamily: "DM Sans, sans-serif",
    fontSize: '1.2rem',
    fontWeight: 600,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        color: themeColors.orange,
        transform: 'translateX(-4px)',
        borderRadius: '40px',
    }
}

const TopNav = ({
    title,
}: TopNavProps) => {
    const navigate = useNavigate();
    const [burgerMenuOpen, setBurgerMenuOpen] = useState(false);
    
    // State for search functionality
    const [searchError, setSearchError] = useState<string | React.ReactNode | null>(null);
    const [loading, setLoading] = useState(false);

    // Handle pub selection from PubSearchGoogle component
    const handlePubSelect = async (selectedPub: GooglePubResult) => {
        try {
            // Show loading spinner
            setLoading(true);

            // Validate coordinates from Google search result
            if (!selectedPub.latitude || !selectedPub.longitude ||
                isNaN(selectedPub.latitude) || isNaN(selectedPub.longitude) ||
                Math.abs(selectedPub.latitude) > 90 || Math.abs(selectedPub.longitude) > 180) {
                console.error("Invalid coordinates from Google search:", selectedPub);
                setSearchError("Invalid location coordinates. Please try another pub.");
                setLoading(false);
                return;
            }

            // First check if this pub already exists in our database by Google Place ID
            const existingPubResponse = await getPubByGooglePlaceId(selectedPub.google_place_id);

            // If pub exists, navigate to it
            if (existingPubResponse.data && existingPubResponse.data.exists) {
                console.log('Pub exists in database:', existingPubResponse.data.pub);
                const existingPub = existingPubResponse.data.pub;

                // Ensure we have all necessary properties consistently named
                const city = existingPub.city || extractCity({
                    name: selectedPub.name,
                    formatted_address: selectedPub.formatted_address || selectedPub.address || '',
                    address_components: []
                });

                // Get coordinates - prefer API values but fall back to selected pub's coordinates
                const latitude = typeof existingPub.latitude === 'number' ? existingPub.latitude :
                                 typeof existingPub.lat === 'number' ? existingPub.lat : selectedPub.latitude;

                const longitude = typeof existingPub.longitude === 'number' ? existingPub.longitude :
                                  typeof existingPub.lng === 'number' ? existingPub.lng : selectedPub.longitude;

                // Ensure all required fields are present with correct naming
                const pubToView = {
                    google_place_id: existingPub.google_place_id,
                    name: existingPub.name || selectedPub.name,
                    latitude: latitude,
                    longitude: longitude,
                    address: existingPub.address || selectedPub.formatted_address || selectedPub.address || '',
                    garden_drawn: existingPub.garden_drawn || false,
                    garden_moderated: existingPub.garden_moderated || false,
                    has_no_garden: existingPub.has_no_garden || false,
                    city: city,
                    distance: existingPub.distance || 0
                };

                console.log('Navigating to existing pub with data:', pubToView);

                // Hide loading spinner
                setLoading(false);
                // Close burger menu
                setBurgerMenuOpen(false);

                // Track exit to pub page
                trackHomePageExit(`pub_${pubToView.google_place_id}`);

                // Navigate to the pub page with consistent pubToView data structure
                navigate(`/pub/${pubToView.google_place_id}`, {
                    state: { pubToView: pubToView }
                });
                return;
            }

            // If we get here, the pub doesn't exist yet, so proceed with creating it
            console.log('Pub not found in database, checking if location is supported');

            // First check if this location is supported by our 3D mesh service
            await fetchMinimalMeshes(selectedPub.latitude, selectedPub.longitude, 11, true);

            // If we get here, the location is supported
            console.log('Location is supported, proceeding to add pub');

            // Use the extractCity utility from cityExtractor
            const city = extractCity({
                name: selectedPub.name,
                formatted_address: selectedPub.formatted_address || selectedPub.address || '',
                address_components: []
            });
            console.log('Extracted city value:', city);

            // Ensure coordinates are properly formatted as numbers
            const latitude = Number(selectedPub.latitude);
            const longitude = Number(selectedPub.longitude);

            // Create the new pub directly
            const createPubPayload = {
                pub: {
                    name: selectedPub.name,
                    address: selectedPub.formatted_address || selectedPub.address || '',
                    latitude: latitude,
                    longitude: longitude,
                    garden_drawn: false,
                    garden_moderated: false,
                    has_no_garden: false,
                    city: city,
                    google_place_id: selectedPub.google_place_id
                }
            };

            console.log('Creating pub with payload:', createPubPayload);

            // Make sure base URL and endpoint are properly joined with a slash
            const baseUrl = config.apiBaseUrl.endsWith('/') ? config.apiBaseUrl : `${config.apiBaseUrl}/`;

            const createResponse = await axios.post(
                `${baseUrl}create_pub/`,
                createPubPayload,
                {
                    headers: {
                        'X-Api-Key': process.env.REACT_APP_DJANGO_SECURE_API_ADMIN_KEY,
                        'Authorization': `Bearer ${AppAuth.getToken()}`
                    }
                }
            );

            if (createResponse.status === 201 && createResponse.data.google_place_id) {
                console.log(`Successfully added pub with UID: ${createResponse.data.google_place_id}`);

                // Create a pub object to navigate to with consistent field names
                const newPub = {
                    name: selectedPub.name,
                    google_place_id: createResponse.data.google_place_id,
                    address: selectedPub.formatted_address || selectedPub.address || '',
                    latitude: latitude,
                    longitude: longitude,
                    garden_drawn: false,
                    garden_moderated: false,
                    has_no_garden: false,
                    city: city,
                    distance: 0
                };

                console.log('Navigating to new pub with data:', newPub);

                // Hide loading
                setLoading(false);
                // Close burger menu
                setBurgerMenuOpen(false);

                // Track exit to pub page
                trackHomePageExit(`pub_${newPub.google_place_id}`);

                // Navigate to the pub page with consistent pubToView format
                navigate(`/pub/${newPub.google_place_id}`, {
                    state: { pubToView: newPub }
                });
            } else {
                throw new Error('Failed to create pub');
            }
        } catch (checkError: any) {
            console.error('Error checking or creating pub:', checkError);
            setLoading(false);

            // Handle the specific flat mesh error from our enhanced API function
            if (checkError.type === 'flat_mesh') {
                setSearchError("We're sorry, this pub isn't currently supported by the services we use to do our calculations. Please try again somewhere else.");
            }
            // Handle specific error from mesh service (legacy format)
            else if (checkError.response?.data?.error && /flat.*triangles/.test(checkError.response.data.error)) {
                setSearchError("We're sorry, this pub isn't currently supported by the services we use to do our calculations. Please try again somewhere else.");
            } else if (checkError.response?.status === 404) {
                setSearchError("We're sorry, this pub isn't currently supported by the services we use to do our calculations. Please try again somewhere else.");
            } else {
                // For other errors, set a generic error message
                const pubName = selectedPub.name || "pub";
                trackPubNotFound(pubName);
                setSearchError(`We couldn't find or add "${pubName}". Please try again later.`);
            }
        }
    };

    return (
        <Box
            sx={{
                top: 0,
                width: '100%',
                color: '#FBE858',
                height: HEADER_HEIGHT,
                position: 'relative',
            }}
        >
        <Fonts />
        <Box
            sx={{
                top: '0',
                marginTop: '10px',
                width: '90%',
                height: HEADER_HEIGHT,
                background: theme.palette.background.paper,
                boxShadow: '0 1px 6px rgba(0, 0, 0, 0.1)',
                position: 'fixed',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                borderRadius: '30px',
                marginLeft: '5%'
            }}
        >
            <Container maxWidth="lg" sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                height: '100%',
                position: 'relative'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Link to="/" style={{ textDecoration: 'none' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '10vh',
                                lineHeight: 1,
                            }}
                        >
                            <img
                                src={suntapLogo}
                                alt="Logo"
                                style={{
                                    height: '50px',
                                }}
                            />
                        </Box>
                    </Link>
                </Box>
                <Stack 
                    direction={'row'} 
                    sx={{ 
                        alignItems: 'center',
                        height: '100%'
                    }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                    }}>
                        <img
                            src={sunIcon}
                            alt="sun_icon"
                            style={{
                                height: '35px',
                                marginRight: '8px',
                                marginTop: '2px' /* Subtle adjustment to fine-tune position */
                            }}
                        />
                    </Box>
                    <IconButton
                        onClick={() => {
                            setBurgerMenuOpen(!burgerMenuOpen)
                        }}
                    >
                        <BurgerMenuIcon />
                    </IconButton>
                </Stack>
            </Container>
        </Box>
        {burgerMenuOpen && (
        <Box
            className="menu-bar"
            sx={{
                position: 'fixed',
                top: 0,
                marginTop: '10px',
                left: '0',
                height: '100vh',
                width: '90%',
                backgroundColor: themeColors.lightBlue,
                backdropFilter: 'blur(10px)',
                zIndex: 1100,
                boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.1)',
                padding: '24px 0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transition: 'all 0.3s ease-in-out',
                marginLeft: '5%',
                borderRadius: '20px',
            }}
        >
            <IconButton
                onClick={() => setBurgerMenuOpen(false)}
                sx={{
                    position: 'absolute',
                    top: '20px',
                    right: '24px',
                    color: themeColors.darkBackground,
                }}
            >
                <CloseIcon />
            </IconButton>
            
            {/* Search Bar Section */}
            <Box sx={{ width: '80%', mb: 2, mt: 6 }}>
                <Typography 
                    variant="h6" 
                    sx={{ 
                        textAlign: 'center', 
                        color: themeColors.darkBackground,
                        fontFamily: "DM Sans, sans-serif",
                        fontWeight: 600,
                        mb: 2
                    }}
                >
                    SEARCH FOR A PUB
                </Typography>
                
                <Box sx={{ position: 'relative' }}>
                    <PubSearchGoogle
                        onPubSelect={handlePubSelect}
                        placeholder="Search for a pub..."
                        onError={setSearchError}
                        onLoading={setLoading}
                        containerSx={{ width: '100%' }}
                        inputSx={{
                            borderRadius: '40px',
                            fontFamily: "DM Sans, sans-serif",
                            border: `2px solid ${themeColors.orange}`,
                            outline: 'none',
                            '.MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                                '.Mui-focused': {
                                    border: 'none',
                                }
                            },
                            '.MuiOutlinedInput-input': {
                                fontFamily: "DM Sans, sans-serif",
                            },
                        }}
                    />
                    
                    {loading && (
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            mt: 2 
                        }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                    
                    {searchError && (
                        <Typography 
                            color="error" 
                            sx={{ 
                                mt: 1, 
                                fontSize: '0.8rem', 
                                textAlign: 'center' 
                            }}
                        >
                            {typeof searchError === 'string' ? searchError : 'Search error'}
                        </Typography>
                    )}
                </Box>
            </Box>
            
            {/* Navigation Menu Items */}
            <Link to="/" style={{ textDecoration: 'none', width: '80%' }}>
                <Typography 
                    variant="h5" 
                    sx={MENU_ITEM_STYLE}
                >
                    HOME
                </Typography>
            </Link>
            <Link to="/search-area" style={{ textDecoration: 'none', width: '80%' }}>
                <Typography 
                    variant="h5" 
                    sx={MENU_ITEM_STYLE}
                >
                    SEARCH AREA
                </Typography>
            </Link>
            <Link to="/pubs" style={{ textDecoration: 'none', width: '80%' }}>
                <Typography 
                    variant="h5" 
                    sx={MENU_ITEM_STYLE}
                >
                    PUBS NEAR YOU
                </Typography>
            </Link>
            <Link to="/about" style={{ textDecoration: 'none', width: '80%' }}>
                <Typography 
                    variant="h5" 
                    sx={MENU_ITEM_STYLE}
                >
                    ABOUT
                </Typography>
            </Link>
            <Link to="/submit" style={{ textDecoration: 'none', width: '80%' }}>
                <Typography 
                    variant="h5" 
                    sx={MENU_ITEM_STYLE}
                >
                    SUBMIT GARDEN
                </Typography>
            </Link>
        </Box>)}
        </Box>
    )
}

export default TopNav;